import React from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Select, Space, Tooltip } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { isSalesUser } from '../utils/inquiryHelpers';
import { useI18n } from '../i18n';

const { Option } = Select;

/**
 * Shared Inquiry header fields used both on the Inquiry creation page and
 * the header-only edit modal: Created by, Client Name, Delivery Address,
 * Priority, Deadline, Requested Delivery Date.
 */
export default function InquiryHeaderFields({
  value,
  onChange,
  onClientChange,
  onAddCustomer,
  leads = [],
  users = [],
  errors = {},
}) {
  const { t } = useI18n();
  const salesUsers = users.filter((u) => isSalesUser(u) && u.active);

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item
            label={t('Created by')}
            required
            validateStatus={errors.createdBy ? 'error' : ''}
            help={errors.createdBy}
          >
            <Select
              showSearch
              placeholder={t('Select a sales user')}
              value={value.createdBy || undefined}
              onChange={(v) => onChange('createdBy', v)}
              optionFilterProp="children"
              allowClear
            >
              {salesUsers.map((user) => (
                <Option key={user.id} value={user.name}>{user.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label={t('Client Name')}
            required
            validateStatus={errors.clientId ? 'error' : ''}
            help={errors.clientId}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Select
                showSearch
                placeholder={t('Search by name, phone, email, or CVR')}
                value={value.clientId}
                onChange={(v) => (onClientChange ? onClientChange(v) : onChange('clientId', v))}
                optionFilterProp="children"
                style={{ width: onAddCustomer ? 'calc(100% - 32px)' : '100%' }}
              >
                {leads.map((lead) => (
                  <Option key={lead.id} value={lead.id}>{lead.name}</Option>
                ))}
              </Select>
              {onAddCustomer && (
                <Tooltip title={t('Add New Customer')}>
                  <Button icon={<UserAddOutlined />} onClick={onAddCustomer} />
                </Tooltip>
              )}
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label={t('Delivery Address')}>
            <Input
              placeholder={t('Auto-filled from client, editable')}
              value={value.deliveryAddress}
              onChange={(e) => onChange('deliveryAddress', e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item label={t('Priority')}>
            <Select value={value.priority} onChange={(v) => onChange('priority', v)}>
              <Option value="High">{t('High')}</Option>
              <Option value="Medium">{t('Medium')}</Option>
              <Option value="Low">{t('Low')}</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label={t('Deadline')}>
            <DatePicker
              value={value.neededBefore}
              onChange={(v) => onChange('neededBefore', v)}
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label={t('Requested Delivery Date')}>
            <DatePicker
              value={value.requestedDeliveryDate}
              onChange={(v) => onChange('requestedDeliveryDate', v)}
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}
